import React from 'react';
import Skeleton from 'src/components/Skeleton/Skeleton';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import CalcomConnectionBadge from 'src/components/CalcomConnectionBadge/CalcomConnectionBadge';
import { ICalcomConflictingIntegration } from 'src/pages/Calendar/CalcomIntegration/types';
import CalcomHostCard from '../CalcomHostCard/CalcomHostCard';
import continueWithCalcom from '../../continue-with-calcom.svg';
import calcomCreateAccountPreview from '../../assets/calcom-create-account-preview.png';
import calcomConnectCalendarPreview from '../../assets/calcom-connect-calendar-preview.png';
import './calcom-connect-card.scss';

interface CalcomConnectCardProps {
  isConnecting: boolean;
  connectError: string | null;
  conflictingIntegration: ICalcomConflictingIntegration | null;
  onConnect: () => void;
}

const CalcomConnectCard = ({
  isConnecting,
  connectError,
  conflictingIntegration,
  onConnect,
}: CalcomConnectCardProps) => {
  return (
    <div className="card card-wide">
      <h3 className="card-title">
        <CalcomConnectionBadge isConnected={false} />
      </h3>
      <h4 className="calcom-connect-heading">
        Connect your calendar for smarter scheduling
      </h4>
      <p className="card-detail">
        Juniper can now book walkthroughs and key pickups directly against your
        live calendar, so recipients only ever see times you&apos;re actually
        free — no back-and-forth, no double-bookings. This works through
        Cal.com, a scheduling platform Juniper connects to.
      </p>
      <p className="card-detail">
        <strong>What is Cal.com?</strong>
      </p>
      <ul className="card-detail-list">
        <li>
          It&apos;s a scheduling platform in its own right — a separate
          third-party product, not part of Juniper.
        </li>
        <li>
          You sign in to Cal.com and connect your own calendars (Google,
          Outlook, etc.) to it.
        </li>
        <li>
          Juniper then plugs into your Cal.com account and pulls your live
          availability into its booking flows.
        </li>
      </ul>
      <p className="card-detail">
        Connecting Cal.com is optional. By default, Juniper schedules with its
        own built-in system; connecting Cal.com books against your live calendar
        instead, so recipients only see times you&apos;re actually free and you
        avoid double-bookings.
      </p>
      <p className="card-detail">
        For now it applies to walkthroughs and key pickups only.
      </p>
      <p className="card-detail">
        You don&apos;t need a Cal.com account ahead of time. Click{' '}
        <strong>Continue with Cal.com</strong> below — if you don&apos;t have
        one, you can create it right there. The first time through, Cal.com
        walks you through a few quick screens to sign up and connect your
        calendars, then brings you back here connected.
      </p>

      {conflictingIntegration && (
        <CalcomHostCard
          isError
          heading="This Cal.com account is already connected"
          lead={
            <>
              The Cal.com account{' '}
              <strong>{conflictingIntegration.calcomUsername}</strong> is already
              connected to another Juniper user — a Cal.com account can only be
              linked to one Juniper user at a time. Reach out to them below and
              ask them to disconnect it in Juniper first; once they do,
              you&apos;ll be able to connect it here.
            </>
          }
          name={`${conflictingIntegration.firstName} ${conflictingIntegration.lastName}`}
          email={conflictingIntegration.email}
          role="Cal.com account owner"
        />
      )}

      {connectError && <p className="error-text">{connectError}</p>}

      {isConnecting ? (
        <Skeleton
          variant="rect"
          width="18.7rem"
          height="3.2rem"
          className="connect-calcom-loading"
        >
          Redirecting to Cal.com…
        </Skeleton>
      ) : (
        <button
          type="button"
          className="connect-calcom-button"
          onClick={() => onConnect()}
          aria-label="Continue with Cal.com"
        >
          <img src={continueWithCalcom} alt="Continue with Cal.com" />
        </button>
      )}

      <div className="calcom-connect-docs">
        <p className="calcom-connect-docs-lead">
          Prefer to read ahead? These Cal.com guides cover the same steps:
        </p>

        <div className="calcom-connect-docs-links">
          <LinkPreviewCard
            href="https://cal.com/help/quick-start/create-account"
            title="Create your Cal.com account"
            description="Sign up and link your Google or Outlook calendar — a quick one-time setup."
            siteName="Cal.com Help"
            image={calcomCreateAccountPreview}
          />

          <LinkPreviewCard
            href="https://cal.com/help/quick-start/calendar"
            title="Connect your calendars"
            description="Link Google, Outlook, Apple, and more to your Cal.com account so availability stays in sync."
            siteName="Cal.com Help"
            image={calcomConnectCalendarPreview}
          />
        </div>
      </div>
    </div>
  );
};

export default CalcomConnectCard;
